import { useEffect, useState } from "react";
import { ExternalLink, Star } from "lucide-react";
import business from "../data/business.json";
import reviewsData from "../data/reviews.json";
import { API } from "../lib/api";
import { SectionTitle } from "./SectionTitle";
import { Reveal } from "./Reveal";

const fmt = (n) => Number(n).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

export const Stars = ({ value, size = 16 }) => (
    <div
        className="relative inline-flex shrink-0"
        role="img"
        aria-label={`Valutazione ${String(value).replace(".", ",")} su 5`}
    >
        <div className="flex gap-0.5 text-[#d8cfc0]">
            {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} width={size} height={size} fill="currentColor" strokeWidth={0} className="shrink-0" />
            ))}
        </div>
        <div
            className="absolute inset-0 overflow-hidden flex gap-0.5 text-[#c33a2c]"
            style={{ width: `${(value / 5) * 100}%` }}
            aria-hidden="true"
        >
            {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} width={size} height={size} fill="currentColor" strokeWidth={0} className="shrink-0" />
            ))}
        </div>
    </div>
);

const StaticCard = ({ r }) => (
    <figure className="bg-avorio2 border border-[#e2d3b4] rounded-xl p-5 flex flex-col" data-testid="review-card">
        <blockquote className="flex-1 text-[15.5px] leading-relaxed">
            <span aria-hidden="true" className="block font-heading text-3xl leading-none text-[#c33a2c] mb-1">
                “
            </span>
            {r.testo}
            {r.estratto ? "…" : ""}
        </blockquote>
        <figcaption className="mt-4">
            <span className="font-semibold text-sm">{r.autore}</span>
            {r.estratto && (
                <a
                    href={business.links.mapsPlace}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-[13px] underline text-[#1f6b80]"
                    data-testid="review-read-more-link"
                >
                    Leggi tutta la recensione su Google <ExternalLink className="w-3 h-3" />
                </a>
            )}
        </figcaption>
    </figure>
);

const GoogleCard = ({ r }) => (
    <figure className="bg-avorio2 border border-[#e2d3b4] rounded-xl p-5 flex flex-col" data-testid="review-card-google">
        <div className="flex items-center gap-3">
            {r.authorPhotoUri && (
                <img
                    src={r.authorPhotoUri}
                    alt=""
                    width={36}
                    height={36}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    className="rounded-full"
                />
            )}
            <div className="min-w-0">
                <figcaption className="font-semibold text-sm truncate">{r.author || "Utente Google"}</figcaption>
                <p className="text-xs text-inchiostro/60">{r.relativeTime}</p>
            </div>
            <div className="ml-auto">
                <Stars value={r.rating ?? 5} size={13} />
            </div>
        </div>
        <blockquote className="mt-3 flex-1 text-[15.5px] leading-relaxed">{r.text}</blockquote>
        <a
            href={r.uri || business.links.mapsPlace}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex items-center gap-1 text-xs underline text-[#1f6b80]"
            data-testid="review-google-link"
        >
            Recensione su Google <ExternalLink className="w-3 h-3" />
        </a>
    </figure>
);

export const Reviews = () => {
    const [google, setGoogle] = useState(null);
    useEffect(() => {
        let alive = true;
        fetch(`${API}/google/place`)
            .then((r) => r.json())
            .then((d) => {
                if (alive && d && d.source === "google" && Array.isArray(d.reviews) && d.reviews.length) setGoogle(d);
            })
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, []);

    const rating = google?.rating ?? business.rating;
    const count = google?.userRatingCount ?? business.reviewCount;
    const list = google?.reviews?.length ? google.reviews : reviewsData;
    const isGoogle = Boolean(google?.reviews?.length);

    return (
        <section id="recensioni" className="bg-avorio text-inchiostro py-20 md:py-28">
            <div className="container-pub">
                <Reveal>
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
                        <SectionTitle light eyebrow="Recensioni" title="Dicono di noi">
                            <div className="mt-6 flex items-center gap-4" data-testid="reviews-aggregate">
                                <span className="font-heading font-semibold text-5xl leading-none">{fmt(rating)}</span>
                                <div>
                                    <Stars value={Number(rating)} size={18} />
                                    <p className="mt-1 text-sm">{count.toLocaleString("it-IT")} recensioni su Google</p>
                                </div>
                            </div>
                            <p className="mt-2 text-xs text-inchiostro/60">Valutazioni e recensioni: Google</p>
                        </SectionTitle>
                        <div className="flex flex-wrap gap-3 shrink-0">
                            <a
                                className="btn btn-dark"
                                href={business.links.mapsPlace}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="reviews-all-btn"
                            >
                                Leggi tutte le recensioni su Google
                            </a>
                            <a
                                className="btn !bg-transparent !border-[#cbbd9e] !text-inchiostro hover:!border-inchiostro"
                                href={business.links.writeReview}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="review-write-btn"
                            >
                                Lascia una recensione
                            </a>
                        </div>
                    </div>
                </Reveal>
                <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="reviews-grid">
                    {list.map((r, i) =>
                        isGoogle ? <GoogleCard key={i} r={r} /> : <StaticCard key={i} r={r} />
                    )}
                </div>
                <p className="mt-6 text-sm text-inchiostro/70" data-testid="reviews-source-note">
                    {isGoogle
                        ? "Recensioni live dalla scheda Google del locale, aggiornate ogni 24 ore."
                        : `Estratti dalle recensioni pubblicate su Google (scheda ufficiale del locale).`}
                </p>
            </div>
        </section>
    );
};